// The home page's cards: featured events with their art behind them, wide
// open-registration cards, the hero's live match and what's up next, and
// the four compact panels along the bottom (schedule, top teams, results,
// announcements). Each panel carries its own heading and "View all".
import { Link } from 'react-router'
import { ArrowRight, BadgeCheck, CalendarDays, Megaphone, Pin, Star, Trophy, Users } from 'lucide-react'
import { BannerArt } from '../brand/BannerArt.jsx'
import { TeamMark } from '../brand/TeamMark.jsx'
import { Card, Pill } from '../ui/primitives.jsx'
import { accentStyle, cn, formatLabel, formatPrize, platformsLabel, plural, roundLabel } from '../../lib/format.js'
import { formatDateRange, formatDay, formatFull, formatTime, relativeTime } from '../../lib/time.js'
import { eventStatus } from './EventCards.jsx'
import { matchHref } from './Matches.jsx'

function Art({ event, className }) {
  return event.bannerUrl ? (
    <img src={event.bannerUrl} alt="" loading="lazy" className={cn('absolute inset-0 size-full object-cover', className)} />
  ) : (
    <BannerArt seed={event.slug} accent={event.accent} className={cn('absolute inset-0 size-full', className)} />
  )
}

// The round arrow in a card's corner. The card's own link covers the card.
function Go({ className }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid size-9 shrink-0 place-items-center rounded-full bg-page/70 ring-1 ring-white/15 backdrop-blur transition-colors group-hover:bg-primary-500 group-hover:text-page',
        className,
      )}
    >
      <ArrowRight className="size-4" />
    </span>
  )
}

function Meta({ icon: Icon, label, children, className }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <Icon aria-hidden="true" className="size-3.5 shrink-0 text-ink-muted" />
      <span className="sr-only">{label}: </span>
      {children}
    </span>
  )
}

const STATUS_PILL = {
  live: 'bg-red-500/90 text-white',
  open: 'bg-emerald-500/90 text-emerald-950',
  upcoming: 'bg-secondary-500/90 text-page',
  completed: 'bg-slate-500/90 text-white',
  cancelled: 'bg-slate-500/90 text-white',
}

// A featured event: its art fills the card, the details sit along the bottom.
export function HomeEventCard({ event, now }) {
  const status = eventStatus(event, now)
  const prize = formatPrize(event.prizePool)
  return (
    <article style={accentStyle(event.accent)} className="group relative isolate flex h-48 flex-col justify-between overflow-hidden rounded-2xl p-4 ring-1 ring-white/10 sm:p-5">
      <Art event={event} className="-z-20 transition-transform duration-500 group-hover:scale-[1.04]" />
      <div aria-hidden="true" className="absolute inset-0 -z-10 bg-gradient-to-t from-page via-page/75 to-page/10" />
      <div className="flex flex-wrap gap-1.5">
        {event.featuredRank && (
          <span className="inline-flex items-center gap-1 rounded-md bg-primary-500 px-2 py-0.5 text-[10px] font-bold tracking-[0.12em] text-page uppercase">
            <Star aria-hidden="true" className="size-3" /> Featured
          </span>
        )}
        <span className={cn('rounded-md px-2 py-0.5 text-[10px] font-bold tracking-[0.12em] uppercase', STATUS_PILL[status.status])}>{status.label}</span>
      </div>
      <div className="flex items-end gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-xl leading-tight font-semibold tracking-wide">
            <Link to={`/e/${event.slug}`} className="rounded after:absolute after:inset-0 after:rounded-2xl">
              {event.name}
            </Link>
          </h3>
          <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-ink-muted">
            <span className="truncate">{event.org.name}</span>
            {event.org.verified && <BadgeCheck aria-label="verified organizer" className="size-3.5 shrink-0 text-secondary-400" />}
            <span aria-hidden="true">·</span>
            <span className="shrink-0">{event.regions.join(', ') || 'Global'}</span>
          </p>
          <p className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink">
            <Meta icon={CalendarDays} label="Dates">
              {formatDateRange(event.startsAt, event.endsAt)}
            </Meta>
            <Meta icon={Users} label="Teams">
              {plural(event.teamCount, 'team')}
            </Meta>
            {prize && (
              <Meta icon={Trophy} label="Prize pool">
                <span className="font-semibold text-primary-300">{prize}</span>
              </Meta>
            )}
          </p>
        </div>
        <Go />
      </div>
    </article>
  )
}

// An event taking sign-ups: art on the left, details on the right.
export function HomeRegistrationCard({ event, now }) {
  const cap = event.teamCap
  return (
    <article style={accentStyle(event.accent)} className="group relative grid grid-cols-[7rem_minmax(0,1fr)] overflow-hidden rounded-2xl bg-surface ring-1 ring-white/10 transition-colors hover:ring-white/20 sm:grid-cols-[11rem_minmax(0,1fr)]">
      <div className="relative overflow-hidden">
        <Art event={event} className="transition-transform duration-500 group-hover:scale-[1.05]" />
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-surface" />
      </div>
      <div className="flex min-w-0 flex-col gap-2.5 p-4">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-lg leading-tight font-semibold tracking-wide uppercase">
              <Link to={`/e/${event.slug}`} className="rounded after:absolute after:inset-0 after:rounded-2xl">
                {event.name}
              </Link>
            </h3>
            <p className="mt-0.5 truncate text-xs text-ink-muted">{event.org.name}</p>
          </div>
          <Go className="size-8" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Pill>{event.regions.join(', ') || 'Global'}</Pill>
          <Pill>{platformsLabel(event.platforms)}</Pill>
          <Pill>{formatLabel(event.stages)}</Pill>
        </div>
        <div className="mt-auto flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs">
          <Meta icon={Users} label="Teams">
            {cap ? `${event.teamCount} / ${cap} teams` : plural(event.teamCount, 'team')}
          </Meta>
          <span className="font-semibold text-emerald-300">Closes {relativeTime(event.registration.closesAt, now)}</span>
        </div>
      </div>
    </article>
  )
}

// ------------------------------------------------------------ hero card

function Side({ team, align }) {
  return (
    <div className={cn('flex min-w-0 flex-col items-center gap-2 text-center', align)}>
      <TeamMark team={team} size="lg" />
      {team ? (
        <Link to={`/t/${team.slug}`} className="max-w-full truncate rounded text-sm font-semibold hover:underline">
          {team.name}
        </Link>
      ) : (
        <span className="text-sm text-ink-muted italic">TBD</span>
      )}
    </div>
  )
}

// The match being played now: both teams and the series score.
export function LiveMatch({ match }) {
  return (
    <div style={accentStyle(match.event.accent)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/e/${match.event.slug}`} className="block truncate rounded font-display text-lg font-semibold tracking-wide hover:underline">
            {match.event.name}
          </Link>
          <p className="text-xs text-ink-muted">
            {match.stageName} · {roundLabel(match)} · Best of {match.bestOf}
          </p>
        </div>
        <Link to={matchHref(match)} className="group inline-flex shrink-0 items-center gap-1 rounded text-sm font-medium text-primary-300 hover:text-primary-400">
          Watch live <ArrowRight aria-hidden="true" className="size-4 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>
      <div className="mt-4 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 rounded-xl bg-page/50 px-3 py-4 ring-1 ring-white/10 ring-inset">
        <Side team={match.team1} />
        <p className="font-display text-4xl font-semibold tabular-nums" aria-label={`Series score ${match.team1?.score ?? 0} to ${match.team2?.score ?? 0}`}>
          {match.team1?.score ?? 0}
          <span className="mx-2 text-ink-muted">–</span>
          {match.team2?.score ?? 0}
        </p>
        <Side team={match.team2} />
      </div>
    </div>
  )
}

function VersusSide({ team }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <TeamMark team={team} size="xs" className="max-sm:hidden" />
      <span className={cn('truncate', !team && 'text-ink-muted italic')}>{team?.name ?? 'TBD'}</span>
    </span>
  )
}

function Versus({ match }) {
  return (
    <span className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 text-sm">
      <VersusSide team={match.team1} />
      <span className="text-[10px] font-semibold text-ink-muted uppercase">vs</span>
      <VersusSide team={match.team2} />
    </span>
  )
}

// What's next in the hero card: when, who, and where.
export function UpNextList({ matches, now }) {
  return (
    <ul className="space-y-2">
      {matches.map((m) => (
        <li key={m.id} className="relative grid grid-cols-[4.25rem_minmax(0,1fr)] items-center gap-3 rounded-xl bg-white/[0.03] px-3 py-2.5 ring-1 ring-white/[0.07] ring-inset hover:bg-white/[0.05]">
          <time dateTime={m.scheduledAt} title={formatFull(m.scheduledAt)} className="text-xs leading-tight text-ink-muted">
            {formatDay(m.scheduledAt, now)}
            <span className="block text-sm font-medium text-ink">{formatTime(m.scheduledAt)}</span>
          </time>
          <div className="min-w-0">
            <Versus match={m} />
            <Link to={matchHref(m)} className="mt-1 block truncate rounded text-xs text-ink-muted after:absolute after:inset-0 after:rounded-xl hover:text-ink">
              {m.event.name} · {roundLabel(m)}
            </Link>
          </div>
        </li>
      ))}
    </ul>
  )
}

// ------------------------------------------------------------ bottom panels

// A compact card with its own heading, as along the bottom of the home page.
export function Panel({ id, icon: Icon, tone = 'text-primary-400', title, description, link, children, className }) {
  return (
    <Card as="section" aria-labelledby={id} className={cn('flex min-w-0 flex-col p-4', className)}>
      <div className="mb-3 flex items-start gap-3">
        <Icon aria-hidden="true" className={cn('mt-0.5 size-6 shrink-0', tone)} />
        <div className="min-w-0 flex-1">
          <h2 id={id} className="font-display text-base leading-tight font-semibold tracking-wide uppercase 2xl:text-lg">
            {title}
          </h2>
          {description && <p className="text-xs text-ink-muted">{description}</p>}
        </div>
        {link && (
          <Link to={link.to} className="group mt-0.5 inline-flex shrink-0 items-center gap-1 rounded text-xs font-medium text-primary-300 hover:text-primary-400">
            {link.label}
            <ArrowRight aria-hidden="true" className="size-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        )}
      </div>
      {children}
    </Card>
  )
}

const short = (iso) => new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(iso))
const rowClass = 'relative rounded-lg px-2 py-2 hover:bg-white/[0.03]'

export function SchedulePanelList({ matches }) {
  return (
    <ol aria-label="Upcoming matches" className="divide-y divide-white/[0.06]">
      {matches.map((m) => (
        <li key={m.id} className={cn(rowClass, 'grid grid-cols-[3.25rem_minmax(0,1fr)] items-center gap-2.5')}>
          <time dateTime={m.scheduledAt} title={formatFull(m.scheduledAt)} className="text-[11px] leading-tight font-semibold text-ink uppercase">
            {short(m.scheduledAt)}
            <span className="block font-normal text-ink-muted normal-case">{formatTime(m.scheduledAt)}</span>
          </time>
          <div className="min-w-0">
            <Link to={matchHref(m)} className="block truncate rounded text-[13px] after:absolute after:inset-0 after:rounded-lg">
              {m.team1?.name ?? 'TBD'} <span className="text-ink-muted">vs</span> {m.team2?.name ?? 'TBD'}
            </Link>
            <p className="mt-0.5 truncate text-xs text-ink-muted" style={accentStyle(m.event.accent)}>
              <span className="tint-text">{m.event.name}</span> · {roundLabel(m)}
            </p>
          </div>
        </li>
      ))}
    </ol>
  )
}

export function TopTeamsPanelList({ teams }) {
  return (
    <ol className="divide-y divide-white/[0.06]">
      {teams.map((team) => (
        <li key={team.slug} title={`${team.name} · ${team.region}`} className={cn(rowClass, 'flex items-center gap-2.5')}>
          <span className={cn('w-5 text-center font-display font-semibold tabular-nums', team.rank <= 3 ? 'text-primary-400' : 'text-ink-muted')}>
            <span className="sr-only">Rank </span>
            {team.rank}
          </span>
          <TeamMark team={team} size="xs" />
          <Link to={`/t/${team.slug}`} className="min-w-0 flex-1 truncate rounded text-[13px] after:absolute after:inset-0 after:rounded-lg">
            {team.name}
            <span className="sr-only"> ({team.region})</span>
          </Link>
          <span className="w-14 text-right text-sm font-semibold tabular-nums">
            {team.points.toLocaleString()}
            <span className="ml-0.5 text-[11px] font-normal text-ink-muted">pts</span>
          </span>
        </li>
      ))}
    </ol>
  )
}

function ResultTeam({ team, align }) {
  return (
    <span className={cn('truncate', align === 'right' && 'text-right', team?.won ? 'font-semibold text-ink' : 'text-ink-muted')}>
      {team?.name ?? 'TBD'}
      {team?.won && <span className="sr-only"> (winner)</span>}
    </span>
  )
}

export function ResultsPanelList({ matches }) {
  return (
    <ol className="divide-y divide-white/[0.06]">
      {matches.map((m) => (
        <li key={m.id} title={`${m.event.name} · ${roundLabel(m)} · ${short(m.completedAt)}`} className={cn(rowClass, 'grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 text-[13px]')}>
          <ResultTeam team={m.team1} align="right" />
          <Link to={matchHref(m)} className="rounded px-1 font-display font-semibold tabular-nums after:absolute after:inset-0 after:rounded-lg">
            <span className={cn(m.team1?.won && 'text-primary-400')}>{m.team1?.score ?? '–'}</span>
            <span className="mx-1 text-ink-muted">-</span>
            <span className={cn(m.team2?.won && 'text-primary-400')}>{m.team2?.score ?? '–'}</span>
            <span className="sr-only">
              , {m.event.name}, {short(m.completedAt)}
            </span>
          </Link>
          <ResultTeam team={m.team2} />
        </li>
      ))}
    </ol>
  )
}

export function AnnouncementsPanelList({ announcements }) {
  return (
    <ul className="divide-y divide-white/[0.06]">
      {announcements.map((a) => (
        <li key={a.id} className="flex gap-3 px-2 py-2.5">
          <span aria-hidden="true" className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-white/5 ring-1 ring-white/10">
            {a.pinned ? <Pin className="size-3.5 text-primary-300" /> : <Megaphone className="size-3.5 text-secondary-300" />}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <h3 className="min-w-0 flex-1 truncate text-sm font-medium">
                {a.pinned && <span className="sr-only">Pinned: </span>}
                {a.title}
              </h3>
              <time dateTime={a.publishedAt} title={formatFull(a.publishedAt)} className="shrink-0 text-xs text-ink-muted">
                {short(a.publishedAt)}
              </time>
            </div>
            <p className="line-clamp-2 text-xs text-ink-muted">{a.excerpt}</p>
          </div>
        </li>
      ))}
    </ul>
  )
}

